import { useVendorList } from "api/vendor";
import CustomReactSelect from "components/CustomReactSelect";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";

const VendorSelector = ({
    value,
    onChange,
    label = "Vendors",
    placeholder = "Select vendors...",
    isMulti = false,
    required = false,
    disabled = false,
    ...props
}) => {
    const vendorQuery = useVendorList();

    const vendors = useMemo(
        () => (vendorQuery.data ?? []).map(user => ({ label: user.name, value: user.id })),
        [vendorQuery.data]
    );

    const handleFetch = useCallback(async (inputValue = "") => {
        const normalizedInput = inputValue.trim().toLowerCase();
        if (!normalizedInput) {
            return vendors;
        }
        return vendors.filter(option => option.label.toLowerCase().includes(normalizedInput));
    }, [vendors]);

    const getSelectedValue = () => {
        if (!value) return null;
        
        // Handle object format {value: id, label: name}
        if (typeof value === 'object' && value.value !== undefined) {
            if (isMulti) {
                return Array.isArray(value) ? value : [value];
            }
            return value;
        }
        
        // Handle primitive value format
        if (isMulti) {
            if (Array.isArray(value)) {
                return vendors.filter(option => value.includes(option.value));
            }
            return vendors.filter(option => option.value === value);
        }
        return vendors.find(option => option.value === value) || null;
    };

    const handleChange = (selectedOption) => {
        if (isMulti) {
            const selectedValues = selectedOption ? selectedOption.map(opt => opt.value) : [];
            if (onChange) {
                onChange(selectedValues, selectedOption);
            }
        } else {
            // For single select, pass the selectedOption object directly
            if (onChange) {
                onChange(selectedOption, selectedOption);
            }
        }
    };


    return (
        <CustomReactSelect
            async
            label={label}
            isMulti={isMulti}
            isSearchable
            isClearable
            loadOptions={handleFetch}
            defaultOptions={vendors}
            value={getSelectedValue()}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            noOptionsMessage={() => "No vendors found"}
            {...props}
        />
    );
};

VendorSelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    isMulti: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default VendorSelector;